import styled from "styled-components";
import { useEffect, useState } from "react";
import AllyService from "../../../services/ally.service";
import { Reservation, Tour, Ally } from "../../../types/common.types";
import ToursService from "../../../services/tours.service";
import { Summary } from "../../../types/seller";
import { useGlobal } from "../../../context/global";
import { useAlly } from "../../../context/ally";
import m from "../../../styles/molanturTheme";
import { Button, DayStrip, Dialog, Label, Segmented, SelectField, clp, dateFromIso } from "../ui";
import repartir from "../../../utils/repartir";
import AvisoMoverPrivado from "../../common/AvisoMoverPrivado";

// Editar una reserva ya enviada: tour, fecha y si las entradas van incluidas. El precio no se
// escribe: es el de la ficha del aliado para el tour elegido.
export default function EditReservation() {
    const { setIsLoading, setMessage } = useGlobal()
    const { setEditReservationId, editReservationId, setSummary, setNewGroupReservations } = useAlly()
    const [data, setData] = useState<Reservation>({} as Reservation)
    const [inicial, setInicial] = useState<Reservation>()
    const [moverTrip, setMoverTrip] = useState(false)
    const [tours, setTours] = useState<Tour[]>([])
     const [me, setMe] = useState<Ally>()


    useEffect(() => {
        async function getInfo() {
            setIsLoading(true)
            try {
                const response = (await AllyService.getReservation(editReservationId)).data
                setData(response)
                setInicial(response)
            } catch (error) {
                console.log(error)
            }
            try {
                const response = await (await ToursService.list()).data
                setTours(response)
            } catch (error) {
                console.log(error)
            }
            try {
                const responseMe = await AllyService.me()
                setMe(responseMe.data)
            }
            catch (error) {
                console.log(error)
            }
            setIsLoading(false)
        }
        getInfo()
    }, [editReservationId])

    const priceOf = (tourId: string) => me?.tour_prices?.find(tp => tp.tour_id === tourId)?.price || 0

    async function onSubmit() {
        if (data?.reservation_date && data?.tour_id) {
            const price = priceOf(data.tour_id)
            if (!price) {
                setMessage({ type: 'error', content: 'Este tour no tiene un precio asignado para ti. Contacta a la agencia para que lo configure.' })
                setTimeout(() => setMessage(undefined), 8000)
                return
            }
            if (!data.tour_id || !data.reservation_date) return
            if (!tours.some(tour => tour.id === data.tour_id)) return
            const newData: Reservation = {
                tour_id: data.tour_id,
                // Sin tocar la fecha llega como texto de la base: new Date() de ese texto es medianoche UTC
                // y en Chile caería en el día anterior. Se toma el día tal cual, como en la tira de días.
                reservation_date: typeof data.reservation_date === 'string' ? dateFromIso(data.reservation_date) : data.reservation_date,
                tickets: data.tickets,
                price: price,
                ticket_included: data.ticket_included,
                cost: data.cost
            } as Reservation
            try {
                setIsLoading(true)
                // Privado: su precio es el del vehículo y el backend lo reparte entre los huéspedes.
                await AllyService.editReservation(editReservationId, {...newData, vehiculo: !!data.vehiculo, mover_trip: moverTrip} as any)
                if (data.vehiculo) newData.price = repartir(price, data.vehiculo.pasajeros)[0]
                setSummary((prev) => {
                    const oldItem = prev.find(item => item.reservation.id === editReservationId)
                    const filtered = prev.filter(item => item.reservation.id !== editReservationId)
                    return ([...filtered, {...oldItem, reservation: {...oldItem!.reservation, ...newData}}] as Summary[])
                })
                setNewGroupReservations((prev) => {
                    const oldItem = prev.find(item => item.id === editReservationId)
                    const filtered = prev.filter(item => item.id !== editReservationId)
                    return ([...filtered, {...oldItem, ...newData}] as Reservation[])
                })
                setEditReservationId('')
            } catch (error: any) {
                console.log(error)
                if (typeof error?.response?.data === 'string') {
                    setMessage({ type: 'error', content: error.response.data })
                    setTimeout(() => setMessage(undefined), 8000)
                }
            } finally {
                setIsLoading(false)
            }
        }
    }

    // La fecha llega de la base como texto ('2026-10-02T00:00:00.000Z'); la tira de días trabaja con
    // fechas locales, así que se toma solo el día (un new Date() del texto caería en el día anterior en Chile).
    const date = !data.reservation_date ? undefined
        : typeof data.reservation_date === 'string' ? dateFromIso(data.reservation_date) : data.reservation_date
    const price = data.tour_id ? (priceOf(data.tour_id) || data.price) : 0

    return (
        <Dialog
            title="Editar reserva"
            onClose={() => setEditReservationId('')}
            onSubmit={e => { e.preventDefault(); onSubmit() }}
            footer={<Button type="submit" full size="lg" icon="check">Guardar cambios</Button>}
        >
            <Main>
                {
                    tours.length > 0 &&
                    <SelectField
                    label="Tour"
                    options={tours.map(tour => ({ value: tour.id, label: tour.id }))}
                    value={data.tour_id || ''}
                    onChange={e => {
                        const newValue = e.target.value
                        setData(prevData => ({...prevData, tour_id: newValue, price: priceOf(newValue)}))
                    }}
                    />
                }
                {
                    date &&
                    <>
                        <Label className="section">Fecha</Label>
                        <DayStrip
                        day={date}
                        onChange={(newDate: Date) => setData(prevData => ({...prevData, reservation_date: newDate}))}
                        />
                    </>
                }
                <AvisoMoverPrivado
                vehiculo={data.vehiculo}
                cambio={data.tour_id !== inicial?.tour_id || data.reservation_date !== inicial?.reservation_date}
                mismoTour={data.tour_id === inicial?.tour_id}
                moverTrip={moverTrip}
                setMoverTrip={setMoverTrip}
                />
                <Label className="section">Entradas al parque</Label>
                <Segmented
                label="Entradas al parque"
                variant="choice"
                options={[{ value: true, label: 'Entradas incluidas' }, { value: false, label: 'Pagan en el parque' }]}
                value={data.ticket_included ?? undefined}
                onChange={included => setData(prev => ({...prev, ticket_included: included}))}
                />
                <div className="price">
                    <span>{data.vehiculo ? `Precio del privado (vehículo, ${data.vehiculo.pasajeros} huéspedes)` : 'Precio por huésped'}</span>
                    <strong>{price ? clp(price) : 'Sin precio configurado'}</strong>
                </div>
            </Main>
        </Dialog>
    )
}

const Main = styled.div`
    .section { margin: 16px 0 9px; }
    .price {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-top: 18px;
        padding: 14px 16px;
        border-radius: ${m.radius.md};
        background: ${m.color.surface2};
        span {
            font-size: 14px;
            color: ${m.color.inkSoft};
        }
        strong {
            font-family: ${m.font.display};
            font-weight: 600;
            font-size: 20px;
        }
    }
`
